/**
 * useReservations Hook
 *
 * Issue #2471: Orval自動生成hookへの全面移行
 *
 * Orval生成hookの薄いラッパー。呼び出し側の変更を最小化するため
 * 既存のシグネチャを維持しつつ、内部実装をOrval生成関数に委譲する。
 *
 * **Features**:
 * - Automatic loading state via isLoading/isFetching
 * - Error handling with typed errors (apiClient interceptor委譲)
 * - Cache management with 30s stale time (shorter due to real-time nature)
 * - Background refetch on window focus
 *
 * @example
 * ```typescript
 * const { data: reservations, isLoading, error, refetch } = useReservations();
 *
 * if (isLoading) return <Skeleton />;
 * if (error) return <ErrorAlert message={error.message} />;
 *
 * return <ReservationCalendar reservations={reservations} />;
 * ```
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  reservationsControllerFindAll,
  reservationsControllerCreate,
  reservationsControllerUpdate,
  reservationsControllerRemove,
  getReservationsControllerFindAllQueryKey,
} from '@/generated/api/reservations/reservations';
import type { CreateReservationDto } from '@/generated/schemas/createReservationDto';
import type { UpdateReservationDto } from '@/generated/schemas/updateReservationDto';
import { logger } from '@/utils/logger';
import { toLocalDateString } from '@/utils/dateUtils';
import type { ExtendedReservation } from '@/types/salonboard';
import type { ReservationResponseDto } from '@/generated/schemas/reservationResponseDto';

/** DTO変換に必要な最小フィールド（Reservation / ExtendedReservation 共通） */
interface ReservationDtoSource {
  customerId: string;
  // Issue #3790 (#3780): 未割当 (NULL) 取込予約の staffId を透過するため nullable に。
  staffId: string | null;
  serviceId: string;
  equipmentId?: string;
  reservationDate: Date | string;
  startTime: string;
  endTime: string;
  customerRequest?: string;
  internalNotes?: string;
  price?: number;
  /** 割引額（円） */
  discountAmount?: number;
  /** 税率（パーセント単位: 10 = 10%） */
  taxRate?: number;
  source?: string;
}

/** Date | string → YYYY-MM-DD（JST考慮）に変換 */
function toReservationDateString(date: Date | string): string {
  if (date instanceof Date) {
    return toLocalDateString(date);
  }
  const str = String(date);
  const parsed = new Date(str);
  if (!isNaN(parsed.getTime())) {
    return toLocalDateString(parsed);
  }
  throw new RangeError(`Invalid reservationDate: ${str}`);
}

/**
 * Reservation/ExtendedReservation → CreateReservationDto 変換
 * バックエンドDTOの必須/オプションフィールドのみを明示的にpick
 * Issue #2471: Orval生成型 CreateReservationDto に準拠
 */
export function toCreateReservationDto(
  reservation: ReservationDtoSource
): Omit<CreateReservationDto, 'tenantId'> {
  // Issue #3790 (#3780): 新規作成にはスタッフ指定が必須 (backend @IsUUID)。未割当
  // (NULL) の取込予約をこの経路で作成することはないため、null は silent に空文字へ
  // 落とさず loud fail させる (M-1)。
  if (reservation.staffId == null) {
    throw new Error(
      'toCreateReservationDto: staffId is required to create a reservation'
    );
  }
  return {
    customerId: reservation.customerId,
    staffId: reservation.staffId,
    serviceId: reservation.serviceId,
    equipmentId: reservation.equipmentId,
    reservationDate: toReservationDateString(reservation.reservationDate),
    startTime: reservation.startTime,
    endTime: reservation.endTime,
    customerRequest: reservation.customerRequest,
    internalNotes: reservation.internalNotes,
    price: reservation.price ?? 0,
    discountAmount: reservation.discountAmount,
    taxRate: reservation.taxRate ?? 10,
    // source: Orval生成型は CreateReservationDtoSource enum だが、
    // BEのenumと互換性のある文字列を許容するためunknown経由でキャスト
    source: reservation.source as CreateReservationDto['source'],
  };
}

/**
 * Reservation/ExtendedReservation → UpdateReservationDto 変換
 * PartialType(CreateReservationDto) なのでtenantId不要
 * Issue #2471: Orval生成型 UpdateReservationDto に準拠
 */
export function toUpdateReservationDto(
  reservation: ReservationDtoSource
): UpdateReservationDto {
  return {
    customerId: reservation.customerId,
    // Issue #3790 (#3780): null (未割当) は省略して送り、backend 側で既存 staffId を
    // 維持させる (update は `updateDto.staffId || reservation.staffId`)。
    staffId: reservation.staffId ?? undefined,
    serviceId: reservation.serviceId,
    equipmentId: reservation.equipmentId,
    reservationDate: toReservationDateString(reservation.reservationDate),
    startTime: reservation.startTime,
    endTime: reservation.endTime,
    customerRequest: reservation.customerRequest,
    internalNotes: reservation.internalNotes,
    price: reservation.price,
    discountAmount: reservation.discountAmount,
    taxRate: reservation.taxRate,
    source: reservation.source as UpdateReservationDto['source'],
  };
}

/** ISO datetime文字列を安全にDateへ変換。不正値はundefinedを返す */
function toSafeDate(value: string | undefined | null): Date | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    logger.warn('[toSafeDate] Invalid date string', { value });
    return undefined;
  }
  return d;
}

/**
 * Orval生成 ReservationResponseDto → FE内部 ExtendedReservation 変換
 *
 * string→Date変換、optional→requiredデフォルト値設定を集約する。
 * ExtendedReservation は SalonBoardReservation の amount/visitProcessing 等を
 * 継承するが API応答に含まれないため末尾で unknown 経由キャスト。
 */
export function mapApiReservationToExtended(
  dto: ReservationResponseDto
): ExtendedReservation {
  // YYYY-MM-DD文字列をローカルタイムゾーンでパース（UTCずれ防止: PR #2507）
  const reservationDate = new Date(dto.reservationDate + 'T00:00:00');
  if (isNaN(reservationDate.getTime())) {
    logger.error(
      '[mapApiReservationToExtended] Invalid reservationDate from API',
      { reservationId: dto.id, reservationDate: dto.reservationDate }
    );
    throw new RangeError(
      `Invalid reservationDate "${dto.reservationDate}" for reservation ${dto.id}`
    );
  }
  // Backend returns staff relation object (not staffName string field)
  const staffObj = (dto as unknown as Record<string, unknown>).staff as
    | { firstName?: string; lastName?: string; fullName?: string }
    | undefined;
  const resolvedStaffName =
    dto.staffName ||
    staffObj?.fullName ||
    [staffObj?.lastName, staffObj?.firstName].filter(Boolean).join(' ') ||
    '';

  if (!resolvedStaffName && dto.staffId) {
    logger.warn(
      '[mapApiReservationToExtended] Staff name could not be resolved',
      { reservationId: dto.id, staffId: dto.staffId }
    );
  }

  return {
    ...dto,
    reservationDate,
    customerName: dto.customerName ?? '',
    customerPhone: dto.customerPhone ?? '',
    customerEmail: dto.customerEmail ?? '',
    staffName: resolvedStaffName,
    serviceName: dto.serviceName ?? '',
    status: dto.status as ExtendedReservation['status'],
    createdAt: toSafeDate(dto.createdAt),
    updatedAt: toSafeDate(dto.updatedAt),
  } as unknown as ExtendedReservation;
}

/**
 * Query key for reservations - uses Orval-generated key for cache consistency
 * Maintained for backward compatibility with callers using RESERVATIONS_QUERY_KEY
 */
export const RESERVATIONS_QUERY_KEY =
  getReservationsControllerFindAllQueryKey();

/**
 * useReservations Hook
 *
 * Fetches all reservations with TanStack Query (Orval-generated).
 *
 * @returns TanStack Query result with reservations data
 */
export function useReservations() {
  return useQuery<ExtendedReservation[], Error>({
    queryKey: RESERVATIONS_QUERY_KEY,
    queryFn: async () => {
      const result = await reservationsControllerFindAll();
      return result.map(mapApiReservationToExtended);
    },
    staleTime: 30 * 1000, // 30秒間はキャッシュを fresh として扱う (予約は変更が多いため短め)
    refetchOnWindowFocus: true,
  });
}

/**
 * useReservation Hook
 *
 * Fetches a single reservation by ID from the cached reservations list.
 *
 * @param reservationId - The reservation ID to find
 * @returns TanStack Query result with single reservation
 */
export function useReservation(reservationId: string | null) {
  const { data: reservations, ...rest } = useReservations();

  return {
    ...rest,
    data: reservations?.find((r) => r.id === reservationId) ?? null,
  };
}

/**
 * useAddReservation Hook
 *
 * Convenience hook for adding a new reservation.
 *
 * @returns Mutation function and state
 */
export function useAddReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['reservations', 'add'],
    mutationFn: async (reservation: ExtendedReservation) => {
      const dto = toCreateReservationDto(reservation);
      const result = await reservationsControllerCreate(
        dto as CreateReservationDto
      );
      // Orval生成型はvoidだが実際はオブジェクト返却。createはID必須のためthrow
      const body = result as unknown;
      if (!body || typeof body !== 'object') {
        throw new Error('[useAddReservation] API returned no response body');
      }
      return mapApiReservationToExtended(body as ReservationResponseDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESERVATIONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      logger.error('[useAddReservation] 予約作成失敗:', error);
      // トースト表示はapiClient interceptor (handleError) に委譲
    },
  });
}

/**
 * useUpdateReservation Hook
 *
 * Convenience hook for updating an existing reservation.
 *
 * @returns Mutation function and state
 */
export function useUpdateReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['reservations', 'update'],
    mutationFn: async (updatedReservation: ExtendedReservation) => {
      const dto = toUpdateReservationDto(updatedReservation);
      const result = await reservationsControllerUpdate(
        updatedReservation.id,
        dto
      );
      const body = result as unknown;
      if (!body || typeof body !== 'object') {
        throw new Error('[useUpdateReservation] API returned no response body');
      }
      return mapApiReservationToExtended(body as ReservationResponseDto);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESERVATIONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      logger.error('[useUpdateReservation] 予約更新失敗:', error);
    },
  });
}

/**
 * useDeleteReservation Hook
 *
 * Convenience hook for deleting a reservation.
 *
 * @returns Mutation function and state
 */
export function useDeleteReservation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['reservations', 'delete'],
    mutationFn: async (reservationId: string) => {
      await reservationsControllerRemove(reservationId);
      return reservationId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RESERVATIONS_QUERY_KEY });
    },
    onError: (error: Error) => {
      logger.error('[useDeleteReservation] 予約削除失敗:', error);
    },
  });
}

/**
 * useRefreshReservations Hook
 *
 * Provides a function to manually refresh reservations data.
 * Useful for realtime update handlers.
 *
 * @returns Object with refetch function
 */
export function useRefreshReservations() {
  const queryClient = useQueryClient();

  return {
    refresh: () =>
      queryClient.invalidateQueries({ queryKey: RESERVATIONS_QUERY_KEY }),
  };
}
